import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import WabaAccounts from './pages/WabaAccounts';
import ConnectWaba from './pages/ConnectWaba';
import WabaEnvironment from './pages/WabaEnvironment';
import { TemplateExecutionHistory } from './pages/waba/TemplateExecutionHistory';
import CampaignDetails from './pages/waba/CampaignDetails';
import SuperAdminDashboard from './pages/admin/SuperAdminDashboard';
import TenantManagement from './pages/admin/TenantManagement';
import TenantEnvironment from './pages/admin/TenantEnvironment';
import UserManagement from './pages/admin/UserManagement';
import WabaManagement from './pages/admin/WabaManagement';
import Profile from './pages/Profile';
import AuditLogs from './pages/admin/AuditLogs';
import WabaAccessManagement from './pages/admin/WabaAccessManagement';
import AdminDashboard from './pages/admin/AdminDashboard';
import UnifiedDashboard from './pages/admin/UnifiedDashboard';
import ApiKeyManagement from './pages/admin/ApiKeyManagement';
import InvoiceManagement from './pages/admin/InvoiceManagement';
import NotificationManagement from './pages/admin/NotificationManagement';

// Protected Route component
function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) return null;

  return isAuthenticated ? <>{children}</> : <Navigate to="/login" replace />;
}

// Super Admin Only Route
function SuperAdminRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, user, isLoading } = useAuth();

  if (isLoading) return null;
  if (!isAuthenticated) return <Navigate to="/login" replace />;

  // Se não for super_admin, redireciona para /admin/dashboard (AdminRoute decide se pode ou vai para /waba)
  if (user?.role !== 'super_admin') {
    return <Navigate to="/admin/dashboard" replace />;
  }

  return <>{children}</>;
}

// Admin Route (ADMIN or SUPER_ADMIN)
function AdminRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, user, isLoading } = useAuth();

  if (isLoading) return null;
  if (!isAuthenticated) return <Navigate to="/login" replace />;

  // Só user/viewer vão para /waba
  if (user?.role !== 'admin' && user?.role !== 'super_admin') {
    return <Navigate to="/waba" replace />;
  }

  return <>{children}</>;
}

// Public Route component (redirect based on user role)
function PublicRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, user, isLoading } = useAuth();

  // Aguardar carregamento do contexto antes de decidir
  if (isLoading) {
    return null;
  }

  if (!isAuthenticated) {
    return <>{children}</>;
  }

  // Se for super_admin, redireciona para painel super admin
  if (user?.role === 'super_admin') {
    return <Navigate to="/admin" replace />;
  }

  // Se for admin, redireciona para dashboard administrativo
  if (user?.role === 'admin') {
    return <Navigate to="/admin/dashboard" replace />;
  }

  // Caso contrário, vai para waba
  return <Navigate to="/waba" replace />;
}

// Root redirect component - Redireciona baseado no role do usuário
function RootRedirect() {
  const { isAuthenticated, user, isLoading } = useAuth();

  if (isLoading) return null;
  if (!isAuthenticated) return <Navigate to="/login" replace />;

  // Super admin vai para /admin
  if (user?.role === 'super_admin') {
    return <Navigate to="/admin" replace />;
  }

  // Admin vai para /admin/dashboard
  if (user?.role === 'admin') {
    return <Navigate to="/admin/dashboard" replace />;
  }

  // Usuários comuns vão para /waba
  return <Navigate to="/waba" replace />;
}

function AppRoutes() {
  return (
    <Routes>
      <Route
        path="/login"
        element={
          <PublicRoute>
            <Login />
          </PublicRoute>
        }
      />

      {/* Super Admin Routes */}
      <Route
        path="/admin"
        element={
          <SuperAdminRoute>
            <SuperAdminDashboard />
          </SuperAdminRoute>
        }
      />
      <Route
        path="/admin/tenants"
        element={
          <SuperAdminRoute>
            <TenantManagement />
          </SuperAdminRoute>
        }
      />
      <Route
        path="/admin/waba"
        element={
          <SuperAdminRoute>
            <WabaManagement />
          </SuperAdminRoute>
        }
      />
      <Route
        path="/admin/tenants/:id/environment"
        element={
          <SuperAdminRoute>
            <TenantEnvironment />
          </SuperAdminRoute>
        }
      />
      <Route
        path="/admin/api-keys"
        element={
          <SuperAdminRoute>
            <ApiKeyManagement />
          </SuperAdminRoute>
        }
      />
      <Route
        path="/admin/invoices"
        element={
          <SuperAdminRoute>
            <InvoiceManagement />
          </SuperAdminRoute>
        }
      />
      <Route
        path="/admin/notifications"
        element={
          <SuperAdminRoute>
            <NotificationManagement />
          </SuperAdminRoute>
        }
      />

      {/* Regular User Routes (WABA) */}
      <Route
        path="/waba"
        element={
          <ProtectedRoute>
            <WabaAccounts />
          </ProtectedRoute>
        }
      />
      <Route
        path="/waba/connect"
        element={
          <ProtectedRoute>
            <ConnectWaba />
          </ProtectedRoute>
        }
      />
      <Route
        path="/waba/:id/environment"
        element={
          <ProtectedRoute>
            <WabaEnvironment />
          </ProtectedRoute>
        }
      />
      <Route
        path="/waba/:id/templates/:templateId/executions"
        element={
          <ProtectedRoute>
            <TemplateExecutionHistory />
          </ProtectedRoute>
        }
      />
      <Route
        path="/waba/:id/campaigns/:campaignId"
        element={
          <ProtectedRoute>
            <CampaignDetails />
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <Dashboard />
          </ProtectedRoute>
        }
      />

      {/* Profile Route (all authenticated users) */}
      <Route
        path="/profile"
        element={
          <ProtectedRoute>
            <Profile />
          </ProtectedRoute>
        }
      />

      {/* Admin Routes (ADMIN and SUPER_ADMIN only) */}
      <Route
        path="/admin/dashboard"
        element={
          <AdminRoute>
            <AdminDashboard />
          </AdminRoute>
        }
      />
      <Route
        path="/admin/audit-logs"
        element={
          <AdminRoute>
            <AuditLogs />
          </AdminRoute>
        }
      />
      <Route
        path="/admin/waba-access"
        element={
          <AdminRoute>
            <WabaAccessManagement />
          </AdminRoute>
        }
      />
      <Route
        path="/admin/users"
        element={
          <AdminRoute>
            <UserManagement />
          </AdminRoute>
        }
      />
      <Route
        path="/admin/operations"
        element={
          <AdminRoute>
            <UnifiedDashboard />
          </AdminRoute>
        }
      />
      {/* Redirects de rotas antigas para o dashboard unificado */}
      <Route path="/admin/waba-statistics" element={<Navigate to="/admin/operations" replace />} />
      <Route path="/admin/tenant-statistics" element={<Navigate to="/admin/operations" replace />} />
      <Route path="/admin/billing" element={<Navigate to="/admin/operations" replace />} />

      {/* Root and fallback routes */}
      <Route path="/" element={<RootRedirect />} />
      <Route path="*" element={<RootRedirect />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter basename={import.meta.env.VITE_BASE_PATH || '/'}>
      <AuthProvider>
        <AppRoutes />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
